import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Location, NgClass } from '@angular/common';
import { Observable, of, catchError, tap } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileDescription, FileUploadComponent } from 'components/file-upload/file-upload.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { ServiceFormActionsComponent } from 'components/service/service-form-actions/service-form-actions.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { BaseFormComponent } from 'components/form/base-form';
import { ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { IrFormLabelsService } from 'services/ir-form-labels.service';
import { ToastService } from 'services/toast.service';
import { SelectStructuralUnitModalComponent } from 'components/modal/select-structural-unit-modal/select-structural-unit-modal.component';
import { ClientCard } from 'types/client.interface';
import { CLAIM_SCOPES, CLAIM_TYPES, UserStructuralUnitModel } from 'types/user.interface';
import { getStructuralUnitsForClaim } from 'util/user.utility';
import { FileCard } from 'types/file.interface';
import {
  InformationRequestCard,
  InformationRequestTopicResponse,
  InformationRequestTopicType,
  TopicFieldDefinitionResponse,
  CreateBoInformationRequestRequest,
  UpdateBoInformationRequestRequest,
  InformationRequestServiceType,
  SubmissionChannel,
  FILE_OBJECT_TYPE_INFO_REQUEST_DOCUMENT,
  FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION,
  FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT
} from 'types/information-request.interface';

@Component({
  selector: 'app-info-request-form',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    FileUploadComponent,
    ConfirmModalComponent,
    ServiceFormActionsComponent,
    TooltipComponent,
    SelectStructuralUnitModalComponent
  ],
  templateUrl: './info-request-form.component.html',
  styleUrls: ['../../../form/base-form.scss', './info-request-form.component.scss']
})
export class InfoRequestFormComponent extends BaseFormComponent implements OnInit {
  @ViewChild('documentUpload') documentUpload?: FileUploadComponent;
  @Input() client?: ClientCard;
  @Input() application?: InformationRequestCard;
  @Input() topics: InformationRequestTopicResponse[] = [];
  @Input() isSubmittingFiles: boolean = false;
  @Input() savedDocumentFiles?: { files: File[]; descriptions: FileDescription[] };
  @Output() applicationUpdated = new EventEmitter<InformationRequestCard>();
  @Output() formChanged = new EventEmitter<boolean>();
  @Output() draftSaved = new EventEmitter<void>();
  @Output() fileUploading = new EventEmitter<Observable<any>[]>();
  @Output() documentsChanged = new EventEmitter<{ files: File[]; descriptions: FileDescription[] }>();
  @Input() inlineEditMode: boolean = false;
  @Output() inlineEditSaved = new EventEmitter<InformationRequestCard>();

  readonly documentFileType = FILE_OBJECT_TYPE_INFO_REQUEST_DOCUMENT;

  selectedTopic?: InformationRequestTopicResponse;
  blockCount: number = 1;
  isSaving: boolean = false;
  isSelectStructuralUnitModalOpen: boolean = false;
  userStructuralUnits: UserStructuralUnitModel[] = [];
  private pendingDraftNavigateToStep2: boolean = false;
  isConfirmTopicChange: boolean = false;
  pendingTopicType?: InformationRequestTopicType;
  isConfirmDelete: boolean = false;
  isConfirmExit: boolean = false;
  /** Set by markFilesChanged(); a file add/remove leaves the reactive form pristine. */
  private filesTouched: boolean = false;

  /** Repeatable table rows per block for sub-sections (e.g. address table in MilitaryService) */
  tableRowsByBlock: Record<number, Record<string, string>[]> = {};

  /** Tooltip keys for first section title per topic */
  private readonly sectionTooltipMap: Record<string, string> = {
    Employment: 'infoRequestEmploymentSection',
    Education: 'infoRequestEducationSection',
    MilitaryService: 'infoRequestMilitarySection',
    PersonBirth: 'infoRequestPersonBirthSection',
    PersonDeath: 'infoRequestPersonDeathSection',
    Marriage: 'infoRequestMarriageSection',
    Divorce: 'infoRequestDivorceSection',
    CitizenshipNationality: 'infoRequestCitizenshipSection',
    ResidenceRegistration: 'infoRequestResidenceSection',
    Repressions: 'infoRequestRepressionsSection',
    RealEstateUntil1940: 'infoRequestRealEstateUntil1940Section',
    RealEstate: 'infoRequestRealEstateSection',
    NotarialDocuments: 'infoRequestNotarialSection',
    CourtDocuments: 'infoRequestCourtSection',
    Other: 'infoRequestOtherSection'
  };

  /** Tooltip keys for second section title (table section) per topic */
  private readonly tableSectionTooltipMap: Record<string, string> = {
    MilitaryService: 'infoRequestMilitaryAddress',
    ResidenceRegistration: 'infoRequestResidenceAddress'
  };

  get isDraftSaved(): boolean {
    return !!this.application?.id;
  }

  get existingDocumentFiles(): FileCard[] {
    return (this.application?.documents || []).filter(d => d.objectType === this.documentFileType);
  }

  get topicFieldsGroup(): FormGroup {
    return this.form.get('topicFields') as FormGroup;
  }

  get globalFields(): TopicFieldDefinitionResponse[] {
    if (this.hasMultipleBlocks && this.allFieldsAreGlobal) return [];
    return this.getFieldsForBlock(0);
  }

  private readonly DEFAULT_MAX_BLOCKS = 20;

  get hasMultipleBlocks(): boolean {
    if (!this.selectedTopic) return false;
    return this.selectedTopic.maxBlocks > 1;
  }

  get canAddBlocks(): boolean {
    return !!this.selectedTopic;
  }

  get effectiveMaxBlocks(): number {
    if (!this.selectedTopic) return 1;
    return this.selectedTopic.maxBlocks > 1 ? this.selectedTopic.maxBlocks : this.DEFAULT_MAX_BLOCKS;
  }

  get allFieldsAreGlobal(): boolean {
    if (!this.selectedTopic) return false;
    return this.selectedTopic.fields.every(f => f.blockIndex === 0);
  }

  private get tableFieldKeySet(): Set<string> {
    if (!this.selectedTopic) return new Set();
    const allSorted = [...this.selectedTopic.fields].sort((a, b) => a.sortOrder - b.sortOrder);
    const sectionTitles = allSorted.filter(f => f.fieldType === 'section_title');
    if (sectionTitles.length <= 1) return new Set();
    const secondTitle = sectionTitles[1];
    return new Set(
      allSorted
        .filter(f => f.sortOrder > secondTitle.sortOrder && f.fieldType !== 'section_title' && f.blockIndex === 0)
        .map(f => f.fieldKey)
    );
  }

  get blockFields(): TopicFieldDefinitionResponse[] {
    if (!this.selectedTopic) return [];
    const tableKeys = this.tableFieldKeySet;
    if (this.hasMultipleBlocks && this.allFieldsAreGlobal) {
      return this.selectedTopic.fields
        .filter(f => !tableKeys.has(f.fieldKey))
        .sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return this.selectedTopic.fields
      .filter(f => f.blockIndex === 1 && !tableKeys.has(f.fieldKey))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get blockTemplateFields(): TopicFieldDefinitionResponse[] {
    if (!this.selectedTopic) return [];
    const tableKeys = this.tableFieldKeySet;
    if (this.hasMultipleBlocks && this.allFieldsAreGlobal) {
      return this.selectedTopic.fields.filter(f => !tableKeys.has(f.fieldKey));
    }
    if (this.hasMultipleBlocks) {
      return this.selectedTopic.fields.filter(f => f.blockIndex === 1 && !tableKeys.has(f.fieldKey));
    }
    // For single-block topics, use all non-section fields as template for additional blocks
    return this.selectedTopic.fields.filter(f => f.fieldType !== 'section_title' && !tableKeys.has(f.fieldKey));
  }

  /** Fields in the primary (first) section — before any second section_title */
  get primaryGlobalFields(): TopicFieldDefinitionResponse[] {
    const fields = this.globalFields;
    const sectionTitles = fields.filter(f => f.fieldType === 'section_title');
    if (sectionTitles.length <= 1) return fields;
    const secondTitle = sectionTitles[1];
    return fields.filter(f => f.sortOrder < secondTitle.sortOrder);
  }

  /** Table sub-section: fields after a non-first section_title, rendered as repeatable table rows */
  get tableSection(): { title: TopicFieldDefinitionResponse; fields: TopicFieldDefinitionResponse[] } | null {
    if (!this.selectedTopic) return null;
    const allSorted = [...this.selectedTopic.fields].sort((a, b) => a.sortOrder - b.sortOrder);
    const sectionTitles = allSorted.filter(f => f.fieldType === 'section_title');
    if (sectionTitles.length <= 1) return null;
    const secondTitle = sectionTitles[1];
    const tableFields = allSorted.filter(
      f => f.sortOrder > secondTitle.sortOrder && f.fieldType !== 'section_title' && f.blockIndex === 0
    );
    if (tableFields.length === 0) return null;
    return { title: secondTitle, fields: tableFields };
  }

  get isTableSectionRequired(): boolean {
    if (!this.tableSection) return false;
    return this.tableSection.title.isRequired || this.tableSection.fields.some(f => f.isRequired);
  }

  constructor(
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    protected override location: Location,
    protected override router: Router,
    protected override toastService: ToastService,
    protected override translateService: TranslateService,
    private authService: AuthorizationService,
    public labels: IrFormLabelsService
  ) {
    super(location, router, formBuilder, apiService, toastService, translateService);
  }

  linkifyDescription(text: string | null | undefined): string {
    if (!text) return '';
    return text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  }

  override ngOnInit(): void {
    this.labels.ensureLoaded();
    this.fillForm();

    if (this.application?.topicType) {
      this.selectedTopic = this.topics.find(t => t.topicType === this.application!.topicType);
      if (this.selectedTopic) {
        this.buildFieldControls();
        this.initTableRows();
        // Restore additional blocks from saved blockCount
        const savedBlockCount = this.application?.blockCount || 1;
        for (let i = 1; i < savedBlockCount; i++) {
          this.addBlock();
        }
        this.populateFieldValues();
      }
    }

    this.form.markAsPristine();
    this.formChanged.emit(false);
    this.form.valueChanges.subscribe(() => {
      this.formChanged.emit(this.form.dirty);
    });
    Promise.resolve().then(() => {
      this.form.markAsPristine();
      this.formChanged.emit(false);
    });
  }

  override back(confirmed: boolean = false): void {
    if ((!this.form.dirty && !this.filesTouched) || confirmed) {
      this.formChanged.emit(false);
      this.location.back();
    } else {
      this.isOpenConfirmBack = true;
    }
  }

  /** A file operation never touches the reactive form, so the wizard has to be told separately. */
  markFilesChanged(): void {
    this.filesTouched = true;
    this.formChanged.emit(true);
  }

  override fillForm(): void {
    this.form = this.formBuilder.group({
      topicType: [this.application?.topicType || null],
      topicFields: this.formBuilder.group({})
    });
  }

  getFieldsForBlock(blockIndex: number): TopicFieldDefinitionResponse[] {
    if (!this.selectedTopic) return [];
    return this.selectedTopic.fields
      .filter(f => f.blockIndex === blockIndex && this.isFieldVisible(f))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  getGlobalSectionTitle(): string {
    const firstField = this.globalFields.find(f => f.sectionTitle);
    return firstField?.sectionTitle || '';
  }

  getGlobalSectionTooltip(): string {
    if (!this.selectedTopic) return '';
    return this.sectionTooltipMap[this.selectedTopic.topicType] || '';
  }

  getTableSectionTooltip(): string {
    if (!this.selectedTopic) return '';
    return this.tableSectionTooltipMap[this.selectedTopic.topicType] || '';
  }

  getBlockSectionTitle(): string {
    const firstBlockField = this.blockFields.find(f => f.sectionTitle);
    return firstBlockField?.sectionTitle || '';
  }

  getBlockSectionTooltip(): string {
    if (!this.selectedTopic) return '';
    return this.sectionTooltipMap[this.selectedTopic.topicType] || '';
  }

  getBlockCardTitle(): string {
    return this.selectedTopic?.blockLabel ?? this.selectedTopic?.name ?? '';
  }

  getAddBlockLabel(): string {
    return this.translateService.instant(`pages.service.infoRequest.addBlockLabels.${this.selectedTopic?.topicType}`) || '';
  }

  isFieldVisible(field: TopicFieldDefinitionResponse): boolean {
    if (!field.isConditional) return true;
    if (!field.conditionalOn) return true;

    for (let b = 0; b < this.blockCount; b++) {
      const controlKey = this.getFieldControlKey(field.conditionalOn, b > 0 ? b + 1 : (field.blockIndex > 0 ? field.blockIndex : 0));
      const control = this.topicFieldsGroup.get(controlKey);
      if (control && this.matchesConditionalValue(control.value, field.conditionalValue)) return true;
    }

    const globalKey = this.getFieldControlKey(field.conditionalOn, 0);
    const globalControl = this.topicFieldsGroup.get(globalKey);
    if (globalControl && this.matchesConditionalValue(globalControl.value, field.conditionalValue)) return true;

    return false;
  }

  private matchesConditionalValue(controlValue: any, conditionalValue: string | null | undefined): boolean {
    if (conditionalValue == null || controlValue == null) return false;
    return String(controlValue) === String(conditionalValue);
  }

  private hasAnyTopicFieldValue(): boolean {
    const controls = this.topicFieldsGroup.controls;
    for (const key of Object.keys(controls)) {
      const value = controls[key].value;
      if (value !== null && value !== '' && value !== false) return true;
    }
    if (Object.values(this.tableRowsByBlock).some(rows => rows.some(row => Object.values(row).some(v => v !== '')))) return true;
    return false;
  }

  /**
   * LNA-2000: changing the topic on a draft also resets every answer past this step -- the client
   * and payer step, the portal consents, and the step-2 uploads (pilnvarojums / miršanas apliecība,
   * atlaides datne), which the backend deletes -- so the confirmation has to account for those too.
   * Gating it on the topic fields alone let the reset run with no warning at all whenever the
   * operator had filled in a later step but nothing on step 1.
   *
   * Excluded because ChangeTopic leaves them alone: the administrative metadata (letter number,
   * subject, additional text, channel, must-complete-by, consolidation, responsible units) and the
   * payment-type flags. The step-1 documents are excluded too -- they survive a topic change.
   *
   * Included, unlike in the portal: the phone and the four contact-person fields. The portal can
   * leave them out because ChangeTopic re-seeds the phone from the client profile, but the
   * backoffice calls it with `requesterProfilePhone: null` ("Backoffice drafts do not re-seed a
   * profile phone"), and the contact-person block is operator-typed -- required for organisations,
   * with no profile to fall back on. Here a topic change really does destroy that data, so it has
   * to be worth a warning (LNA-1850 R12).
   */
  private hasAnyValueBeyondTopicStep(): boolean {
    const app = this.application;
    if (!app) return false;

    const hasStepTwoDocument = (app.documents || []).some(d =>
      (d.objectType as string) === FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION
      || (d.objectType as string) === FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT);

    return hasStepTwoDocument
      || (app.requesterRelationType !== 'Unknown' && app.requesterRelationType !== 'ForSelf')
      || !!app.requesterForOtherPersonType
      || !!app.requesterForOtherPersonData
      || (app.payerType !== 'Unknown' && app.payerType !== 'Requester')
      || !!app.payerData
      || !!app.payerFreeOfChargeReason
      || app.responseDeliveryMethod !== 'Unknown'
      || !!app.responseDeliveryDetail
      || !!app.responseDeliveryStructuralUnitId
      || app.invoiceDeliveryMethod !== 'Unknown'
      || !!app.invoiceDeliveryDetail
      || !!app.invoiceDeliveryStructuralUnitId
      || app.serviceDeadline !== 'Days30'
      || !!app.deadlineDate
      || !!app.discountGroupType
      || !!app.discountGroupData
      || !!app.purpose
      || !!app.purposeInstitutionName
      || app.vsaaFlag
      || app.requiresRestrictedAccess
      || !!app.notes
      || app.gdprConsent
      || !!app.phone
      || !!app.personalCode
      || !!app.contactPersonName
      || !!app.contactPersonSurname
      || !!app.contactPersonEmail
      || !!app.contactPersonPhone;
  }

  getFieldControlKey(fieldKey: string, blockIndex: number): string {
    if (blockIndex === 0) return fieldKey;
    return `${fieldKey}_block${blockIndex}`;
  }

  getControlKey(field: TopicFieldDefinitionResponse, blockNumber: number): string {
    if (blockNumber > 1) {
      return `${field.fieldKey}_block${blockNumber}`;
    }
    if (field.blockIndex === 0 && !(this.hasMultipleBlocks && this.allFieldsAreGlobal)) {
      return field.fieldKey;
    }
    return field.fieldKey;
  }

  getFieldValue(fieldKey: string, blockNumber: number): string {
    const key = blockNumber <= 1 ? fieldKey : `${fieldKey}_block${blockNumber}`;
    return this.topicFieldsGroup.get(key)?.value || '';
  }

  shouldRenderFieldError(field: TopicFieldDefinitionResponse, blockNumber: number): boolean {
    const key = this.getControlKey(field, blockNumber);
    return this.shouldRenderError(key, this.topicFieldsGroup);
  }

  getFieldErrorMessage(field: TopicFieldDefinitionResponse, blockNumber: number): string | null {
    const key = this.getControlKey(field, blockNumber);
    return this.getErrorMessage(key, this.topicFieldsGroup);
  }

  selectTopic(topic: InformationRequestTopicResponse): void {
    if (this.selectedTopic?.topicType === topic.topicType) return;

    if (this.selectedTopic && (this.hasAnyTopicFieldValue() || this.hasAnyValueBeyondTopicStep())) {
      this.pendingTopicType = topic.topicType;
      this.isConfirmTopicChange = true;
      return;
    }

    this.applyTopicSelection(topic);
  }

  confirmTopicChange(): void {
    this.isConfirmTopicChange = false;
    const topic = this.topics.find(t => t.topicType === this.pendingTopicType);
    if (topic) {
      this.applyTopicSelection(topic);
    }
    this.pendingTopicType = undefined;
  }

  cancelTopicChange(): void {
    this.isConfirmTopicChange = false;
    this.pendingTopicType = undefined;
  }

  private applyTopicSelection(topic: InformationRequestTopicResponse): void {
    this.selectedTopic = { ...topic };
    this.blockCount = 1;
    this.form.get('topicType')?.setValue(topic.topicType);
    this.buildFieldControls();
    this.initTableRows();
    this.form.markAsDirty();
    this.formChanged.emit(true);
  }

  private initTableRows(): void {
    this.tableRowsByBlock = {};
    if (this.tableSection) {
      for (let b = 1; b <= this.blockCount; b++) {
        this.tableRowsByBlock[b] = [this.createEmptyTableRow()];
      }
    }
  }

  private createEmptyTableRow(): Record<string, string> {
    const row: Record<string, string> = {};
    if (this.tableSection) {
      for (const f of this.tableSection.fields) {
        row[f.fieldKey] = '';
      }
    }
    return row;
  }

  private buildFieldControls(): void {
    const fieldsGroup = this.formBuilder.group({});

    if (!this.selectedTopic) return;

    // Global fields (only when NOT all fields are treated as block fields)
    if (!(this.hasMultipleBlocks && this.allFieldsAreGlobal)) {
      const tableFieldKeys = new Set(this.tableSection?.fields.map(f => f.fieldKey) || []);
      const globalFields = this.selectedTopic.fields.filter(f => f.blockIndex === 0);
      for (const field of globalFields) {
        if (field.fieldType === 'section_title') continue;
        if (tableFieldKeys.has(field.fieldKey)) continue;
        const validators = [];
        if (field.maxLength) validators.push(Validators.maxLength(field.maxLength));
        fieldsGroup.addControl(field.fieldKey, this.formBuilder.control('', validators));
      }
    }

    // Block fields
    for (let b = 1; b <= this.blockCount; b++) {
      for (const field of this.blockTemplateFields) {
        if (field.fieldType === 'section_title') continue;
        const key = b === 1 ? field.fieldKey : `${field.fieldKey}_block${b}`;
        const validators = [];
        if (field.maxLength) validators.push(Validators.maxLength(field.maxLength));
        fieldsGroup.addControl(key, this.formBuilder.control('', validators));
      }
    }

    this.form.setControl('topicFields', fieldsGroup);
  }

  addBlock(): void {
    if (!this.selectedTopic) return;
    const maxBlocks = this.selectedTopic.maxBlocks > 1 ? this.selectedTopic.maxBlocks : this.DEFAULT_MAX_BLOCKS;
    if (this.blockCount >= maxBlocks) return;

    this.blockCount++;

    for (const field of this.blockTemplateFields) {
      if (field.fieldType === 'section_title') continue;
      const key = `${field.fieldKey}_block${this.blockCount}`;
      const validators = [];
      if (field.maxLength) validators.push(Validators.maxLength(field.maxLength));
      this.topicFieldsGroup.addControl(key, this.formBuilder.control('', validators));
    }

    if (this.tableSection) {
      this.tableRowsByBlock[this.blockCount] = [this.createEmptyTableRow()];
    }

    this.form.markAsDirty();
    this.formChanged.emit(true);
  }

  removeBlock(blockNumber: number): void {
    if (this.blockCount <= 1) return;

    const templateFields = this.blockTemplateFields;
    const getKey = (fieldKey: string, block: number): string =>
      block === 1 ? fieldKey : `${fieldKey}_block${block}`;

    // Shift values from blocks above the removed one down by one
    for (let b = blockNumber; b < this.blockCount; b++) {
      for (const field of templateFields) {
        const currentKey = getKey(field.fieldKey, b);
        const nextKey = getKey(field.fieldKey, b + 1);
        const nextValue = this.topicFieldsGroup.get(nextKey)?.value || '';
        this.topicFieldsGroup.get(currentKey)?.setValue(nextValue);
      }
    }

    // Remove the last block's controls
    for (const field of templateFields) {
      const lastKey = getKey(field.fieldKey, this.blockCount);
      this.topicFieldsGroup.removeControl(lastKey);
    }

    // Renumber table rows
    if (this.tableSection) {
      const newTableRows: Record<number, Record<string, string>[]> = {};
      let newIdx = 1;
      for (let b = 1; b <= this.blockCount; b++) {
        if (b === blockNumber) continue;
        newTableRows[newIdx] = this.tableRowsByBlock[b] || [this.createEmptyTableRow()];
        newIdx++;
      }
      this.tableRowsByBlock = newTableRows;
    }

    this.blockCount--;
    this.form.markAsDirty();
    this.formChanged.emit(true);
  }

  addTableRow(blockNumber: number): void {
    if (!this.tableSection) return;
    if (!this.tableRowsByBlock[blockNumber]) {
      this.tableRowsByBlock[blockNumber] = [];
    }
    this.tableRowsByBlock[blockNumber].push(this.createEmptyTableRow());
    this.form.markAsDirty();
    this.formChanged.emit(true);
  }

  removeTableRow(blockNumber: number, index: number): void {
    const rows = this.tableRowsByBlock[blockNumber];
    if (!rows || rows.length <= 1) return;
    rows.splice(index, 1);
    this.form.markAsDirty();
    this.formChanged.emit(true);
  }

  getTableRows(blockNumber: number): Record<string, string>[] {
    return this.tableRowsByBlock[blockNumber] || [];
  }

  onTableCellChange(): void {
    this.form.markAsDirty();
    this.formChanged.emit(true);
  }

  private populateFieldValues(): void {
    if (!this.application?.topicFieldData) return;

    try {
      const fieldData = JSON.parse(this.application.topicFieldData);

      for (const [key, value] of Object.entries(fieldData)) {
        const control = this.topicFieldsGroup.get(key);
        if (control) {
          control.setValue(value);
        }
      }

      // Populate table rows per block from stored JSON arrays
      if (this.tableSection && this.tableSection.fields.length > 0) {
        for (let b = 1; b <= this.blockCount; b++) {
          const firstField: TopicFieldDefinitionResponse = this.tableSection.fields[0];
          const dataKey: string = b === 1 ? firstField.fieldKey : `${firstField.fieldKey}_block${b}`;
          const firstVal = fieldData[dataKey];
          if (firstVal) {
            try {
              const arr = JSON.parse(firstVal);
              if (Array.isArray(arr) && arr.length > 0) {
                this.tableRowsByBlock[b] = arr.map((_val: string, idx: number) => {
                  const row: Record<string, string> = {};
                  for (const f of this.tableSection!.fields) {
                    const fKey = b === 1 ? f.fieldKey : `${f.fieldKey}_block${b}`;
                    const fArr = fieldData[fKey] ? JSON.parse(fieldData[fKey]) : [];
                    row[f.fieldKey] = (Array.isArray(fArr) ? fArr[idx] : '') || '';
                  }
                  return row;
                });
              }
            } catch { /* not JSON array, keep single row */ }
          }
        }
      }
    } catch {
      // topicFieldData is not valid JSON
    }
  }

  private collectTopicFieldData(): string {
    const data: Record<string, any> = {};
    const controls = this.topicFieldsGroup.controls;
    const tableFieldKeys = new Set(this.tableSection?.fields.map(f => f.fieldKey) || []);
    // Build set of all table data keys (including block suffixes)
    const allTableDataKeys = new Set<string>();
    if (this.tableSection) {
      for (let b = 1; b <= this.blockCount; b++) {
        for (const f of this.tableSection.fields) {
          allTableDataKeys.add(b === 1 ? f.fieldKey : `${f.fieldKey}_block${b}`);
        }
      }
    }

    for (const key of Object.keys(controls)) {
      if (allTableDataKeys.has(key) || tableFieldKeys.has(key)) continue;
      const value = controls[key].value;
      if (value !== null && value !== undefined && value !== '') {
        data[key] = value;
      }
    }

    // Serialize table rows as JSON arrays per field per block
    const ts = this.tableSection;
    if (ts) {
      const tsFields: TopicFieldDefinitionResponse[] = ts.fields;
      for (let b = 1; b <= this.blockCount; b++) {
        const rows = this.tableRowsByBlock[b] || [];
        for (const f of tsFields) {
          const dataKey: string = b === 1 ? f.fieldKey : `${f.fieldKey}_block${b}`;
          const values: string[] = rows.map(row => row[f.fieldKey] || '');
          data[dataKey] = JSON.stringify(values);
        }
      }
    }

    return JSON.stringify(data);
  }

  saveDraft(): void {
    if (!this.selectedTopic) {
      this.toastService.showDanger('infoRequestTopicRequired', '', { shouldTranslate: true });
      return;
    }

    if (!this.application) {
      this.beginDraftCreate(false);
    } else {
      this.isSaving = true;
      this.saveDraftData();
    }
  }

  /**
   * Resolve the responsible structural unit before creating the draft. Candidates are the
   * operator's own memberships in which they hold the InformationRequest/Create claim: assigned
   * automatically when there is a single eligible unit, or chosen in a modal when several (LNA-1841).
   */
  private beginDraftCreate(navigateToStep2: boolean): void {
    const units = getStructuralUnitsForClaim(
      this.authService.userSecurityInfo(),
      CLAIM_SCOPES['informationRequest'],
      CLAIM_TYPES['create']
    );
    if (units.length > 1) {
      this.userStructuralUnits = units;
      this.pendingDraftNavigateToStep2 = navigateToStep2;
      this.isSelectStructuralUnitModalOpen = true;
      return;
    }
    this.createDraft(units[0]?.id ?? null, navigateToStep2);
  }

  onStructuralUnitSelected(structuralUnitId: string | void): void {
    this.isSelectStructuralUnitModalOpen = false;
    if (typeof structuralUnitId !== 'string') return;
    const navigateToStep2 = this.pendingDraftNavigateToStep2;
    this.pendingDraftNavigateToStep2 = false;
    this.createDraft(structuralUnitId, navigateToStep2);
  }

  onSelectStructuralUnitCancelled(): void {
    this.isSelectStructuralUnitModalOpen = false;
    this.pendingDraftNavigateToStep2 = false;
  }

  private createDraft(responsibleStructuralUnitId: string | null, navigateToStep2: boolean): void {
    this.isSaving = true;
    const isOrganization = this.client?.clientType === 'Organization';
    const createRequest: CreateBoInformationRequestRequest = {
      clientId: this.client!.id!,
      legalEntityId: isOrganization ? this.client!.legalEntityId! : undefined,
      serviceType: 'ArchiveCertificate' as InformationRequestServiceType,
      submissionChannel: 'Email' as SubmissionChannel,
      topicType: this.selectedTopic?.topicType || undefined,
      topicVersionId: this.selectedTopic?.versionId || undefined,
      topicFieldData: this.collectTopicFieldData(),
      blockCount: this.blockCount,
      responsibleStructuralUnitId
    };
    this.apiService.createInformationRequest(createRequest).subscribe({
      next: (data) => {
        this.application = data;
        this.applicationUpdated.emit(data);
        this.isSaving = false;
        this.filesTouched = false;
        if (navigateToStep2) {
          this.draftSaved.emit();
          this.router.navigate([], {
            queryParams: { solis: 2 },
            queryParamsHandling: 'merge'
          });
        } else {
          this.form.markAsPristine();
          this.formChanged.emit(false);
          this.toastService.showSuccess('infoRequestDraftSaved', '', { shouldTranslate: true });
          this.draftSaved.emit();
        }
      },
      error: (error) => {
        console.error(error);
        this.isSaving = false;
        this.toastService.showRequestError(error);
      }
    });
  }

  private saveDraftData(): void {
    const request: UpdateBoInformationRequestRequest = {
      ...this.preserveStep2Fields(),
      topicType: this.selectedTopic?.topicType || undefined,
      topicVersionId: this.selectedTopic?.versionId || undefined,
      topicFieldData: this.collectTopicFieldData(),
      blockCount: this.blockCount
    };

    this.apiService.updateInformationRequest(this.application!.id, request).subscribe({
      next: (data) => {
        this.application = data;
        this.applicationUpdated.emit(data);
        this.isSaving = false;
        this.form.markAsPristine();
        this.filesTouched = false;
        this.formChanged.emit(false);
        this.toastService.showSuccess('infoRequestDraftSaved', '', { shouldTranslate: true });
        this.draftSaved.emit();
      },
      error: (error) => {
        console.error(error);
        this.isSaving = false;
        this.toastService.showRequestError(error);
      }
    });
  }

  goToNextStep(): void {
    this.submitAttempted = true;

    if (!this.selectedTopic) {
      this.toastService.showDanger('infoRequestTopicRequired', '', { shouldTranslate: true });
      return;
    }

    if (this.form.valid) {
      if (!this.application) {
        this.beginDraftCreate(true);
      } else {
        this.isSaving = true;
        this.saveDraftAndNavigate();
      }
    } else {
      // Same form, same reason as in flushStepData(): nothing here is mandatory, only capped.
      this.toastService.showDanger('formHasInvalidValues', '', { shouldTranslate: true });
    }
  }

  private saveDraftAndNavigate(): void {
    const request: UpdateBoInformationRequestRequest = {
      ...this.preserveStep2Fields(),
      topicType: this.selectedTopic?.topicType || undefined,
      topicVersionId: this.selectedTopic?.versionId || undefined,
      topicFieldData: this.collectTopicFieldData(),
      blockCount: this.blockCount,
      letterNumber: this.application?.letterNumber || null,
      aboutSubject: this.application?.aboutSubject || null,
      submissionChannel: this.application?.submissionChannel || undefined,
      requiresRestrictedAccess: this.application?.requiresRestrictedAccess || false
    };

    this.apiService.updateInformationRequest(this.application!.id, request).subscribe({
      next: (data) => {
        this.application = data;
        this.applicationUpdated.emit(data);
        this.draftSaved.emit();
        this.isSaving = false;
        this.filesTouched = false;
        this.router.navigate([], {
          queryParams: { solis: 2 },
          queryParamsHandling: 'merge'
        });
      },
      error: (error) => {
        console.error(error);
        this.isSaving = false;
        this.toastService.showRequestError(error);
      }
    });
  }

  /**
   * Save on the way out of step 1. The card is taken from the response and never from the form:
   * an optimistic merge used to push an unsaved topic into the local card, so the summary showed
   * the abandoned topic while the backend had already run ChangeTopic, and the local `documents`
   * array still listed files the backend had deleted (LNA-1850 R4/R6). An invalid form is refused
   * before anything local changes, and a failed save is now reported instead of swallowed (RF).
   * Both refusals are announced: leaving the step silently threw the operator's edits away (rank 6).
   */
  flushStepData(): Observable<InformationRequestCard | null> {
    if (!this.selectedTopic || !this.application) return of(null);
    if (!this.form.valid) {
      // Not `formHasErrors` ("obligātie lauki"): backoffice step 1 has no required fields by design
      // (LNA-2040), so the only rule that can fail here is a maximum length (LNA-1850).
      this.toastService.showDanger('formHasInvalidValues', '', { shouldTranslate: true });
      return of(null);
    }
    const request: UpdateBoInformationRequestRequest = {
      ...this.preserveStep2Fields(),
      topicType: this.selectedTopic.topicType,
      topicVersionId: this.selectedTopic.versionId,
      topicFieldData: this.collectTopicFieldData(),
      blockCount: this.blockCount,
      letterNumber: this.application.letterNumber || null,
      aboutSubject: this.application.aboutSubject || null,
      submissionChannel: this.application.submissionChannel || undefined,
      requiresRestrictedAccess: this.application.requiresRestrictedAccess || false
    };
    return this.apiService.updateInformationRequest(this.application.id, request).pipe(
      tap((data: InformationRequestCard) => {
        if (data) {
          this.application = data;
          this.filesTouched = false;
          this.applicationUpdated.emit(data);
        }
      }),
      catchError((error) => {
        console.error(error);
        this.toastService.showRequestError(error);
        return of(null);
      })
    );
  }

  onExit(): void {
    if (this.form.dirty || this.filesTouched) {
      this.isConfirmExit = true;
    } else {
      this.router.navigate(['/informacijas-pieprasijums']);
    }
  }

  confirmExit(): void {
    this.isConfirmExit = false;
    this.router.navigate(['/informacijas-pieprasijums']);
  }

  onDelete(): void {
    this.isConfirmDelete = true;
  }

  saveInlineEdit(): void {
    if (!this.application || !this.selectedTopic) return;

    this.isSaving = true;
    const request: UpdateBoInformationRequestRequest = {
      ...this.preserveStep2Fields(),
      topicType: this.selectedTopic.topicType,
      topicVersionId: this.selectedTopic.versionId,
      topicFieldData: this.collectTopicFieldData(),
      blockCount: this.blockCount
    };

    this.apiService.updateInformationRequest(this.application.id, request).subscribe({
      next: (data) => {
        this.application = data;
        this.isSaving = false;
        this.filesTouched = false;
        // No success toast here: the host still has the section's documents to save, and announcing
        // the save before them contradicted a refusal that came a moment later (LNA-2047).
        this.inlineEditSaved.emit(data);
      },
      error: (error) => {
        console.error(error);
        this.isSaving = false;
        this.toastService.showRequestError(error);
      }
    });
  }

  private preserveStep2Fields(): Partial<UpdateBoInformationRequestRequest> {
    const app = this.application;
    if (!app) return {};
    // When the topic changes on a draft, the BE resets the whole draft and ignores any other
    // fields in the request (LNA-1952) — don't re-send the soon-stale step-2 values then.
    if (this.selectedTopic && app.topicType && this.selectedTopic.topicType !== app.topicType) {
      return {};
    }
    return {
      submissionChannel: app.submissionChannel || undefined,
      letterNumber: app.letterNumber || null,
      aboutSubject: app.aboutSubject || null,
      additionalText: app.additionalText || null,
      phone: app.phone || null,
      requesterRelationType: app.requesterRelationType || undefined,
      requesterForOtherPersonType: app.requesterForOtherPersonType || undefined,
      requesterForOtherPersonData: app.requesterForOtherPersonData || null,
      discountGroupType: app.discountGroupType || undefined,
      discountGroupData: app.discountGroupData || null,
      payerType: app.payerType || undefined,
      payerData: app.payerData || null,
      payerFreeOfChargeReason: app.payerFreeOfChargeReason || null,
      invoiceDeliveryMethod: app.invoiceDeliveryMethod || undefined,
      invoiceDeliveryDetail: app.invoiceDeliveryDetail || null,
      invoiceDeliveryStructuralUnitId: app.invoiceDeliveryStructuralUnitId || null,
      purpose: app.purpose || undefined,
      purposeInstitutionName: app.purposeInstitutionName || null,
      vsaaFlag: app.vsaaFlag || false,
      requiresRestrictedAccess: app.requiresRestrictedAccess || false,
      serviceDeadline: app.serviceDeadline || undefined,
      serviceDeadlineDate: app.serviceDeadlineDate || null,
      mustCompleteByDate: app.mustCompleteByDate || null,
      responseDeliveryMethod: app.responseDeliveryMethod || undefined,
      responseDeliveryDetail: app.responseDeliveryDetail || null,
      responseDeliveryStructuralUnitId: app.responseDeliveryStructuralUnitId || null,
      notes: app.notes || null,
      contactPersonName: app.contactPersonName || null,
      contactPersonSurname: app.contactPersonSurname || null,
      contactPersonEmail: app.contactPersonEmail || null,
      contactPersonPhone: app.contactPersonPhone || null
    };
  }

  confirmDelete(): void {
    this.isConfirmDelete = false;
    if (this.application?.id) {
      this.apiService.deleteInformationRequest(this.application.id).subscribe({
        next: () => {
          this.toastService.showSuccess('applicationDraftDeleted', '', { shouldTranslate: true });
          this.router.navigate(['/informacijas-pieprasijums']);
        },
        error: () => {
          this.toastService.showRequestError();
        }
      });
    } else {
      this.router.navigate(['/informacijas-pieprasijums']);
    }
  }
}
